import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { StaffLegacyRoutes } from '../routes/StaffLegacyRoutes';
import { StaffRoutes } from '../routes/StaffRoutes';
import { SuperAdminRoutes } from '../routes/SuperAdminRoutes';
import { useAuth } from '../store/AuthContext';
import {
  hasHelpCenterStaffPortalQuery,
  isHelpCenterRootPath,
} from '../pages/HelpCenter/helpCenterRoutes';
import {
  isHelpCenterStaff,
  resolveWsgPermission,
} from '../pages/HelpCenter/helpCenterPermissions';

interface StaffModeContextProps {
  isStaffMode: boolean;
  toggleStaffMode: () => void;
  toggleModal: () => void;
}

const StaffModeContext = createContext<StaffModeContextProps | undefined>(undefined);

interface StaffModeProviderProps {
  children: ReactNode;
}

export const StaffModeProvider: React.FC<StaffModeProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { tokenDecoded } = useAuth();

  const [isStaffMode, setIsStaffMode] = useState<boolean>(false);

  const isStaffUser = isHelpCenterStaff(
    resolveWsgPermission(tokenDecoded?.extension_WSG_Permissions),
  );


  const isStaffRoute = (currentPath: string, staffPaths: string[]): boolean => {
    return staffPaths.some(staffPath => {
      const regexPattern = staffPath
        .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        .replace(/:[^/]+/g, '[^/]+')
        .replace(/^\//, '^/')
        .replace(/$/, '($|\\?.*$)');
      
      const regex = new RegExp(regexPattern, 'i');
      return regex.test(currentPath);
    });
  };

  useEffect(() => { 
    const excludePaths = ['/manage_events.asp', '/wsg-knowledge-center.asp', '/help.asp']
    if (excludePaths.includes(pathname)) {
      return
    }

    // The Help Center landing page serves both portals from `/help-center`, so the portal comes
    // from the query string instead of the path. Non-staff accounts always get the member portal.
    if (isHelpCenterRootPath(pathname)) {
      setIsStaffMode(isStaffUser && hasHelpCenterStaffPortalQuery(search));
      return;
    }

    const staffPaths = [
      ...StaffLegacyRoutes.map(r => r.path).filter((path): path is string => Boolean(path)),
      ...StaffRoutes.map(s => s.path).filter((path): path is string => Boolean(path)),
      ...SuperAdminRoutes.map(s => s.path).filter((path): path is string => Boolean(path))
    ];
    const isManageFirmSection =
      /^\/ManageFirm(\/|$)/i.test(pathname) ||
      /^\/manageFirm(\/|$)/i.test(pathname);
    setIsStaffMode(
      isStaffRoute(pathname, staffPaths) || isManageFirmSection
    );
  }, [pathname, search, isStaffUser]);

  useEffect(() => {
    document.documentElement.classList.toggle("portal-staff", isStaffMode);
    return () => {
      document.documentElement.classList.remove("portal-staff");
    };
  }, [isStaffMode]);

  const toggleStaffMode = () => {
    localStorage.setItem('isStaffMode', String(!isStaffMode));
    navigate(!isStaffMode ? '/ManageFirm' : '/mywsg');
  };

  return (
    <StaffModeContext.Provider
      value={{
        isStaffMode,
        toggleStaffMode,
        toggleModal: () => {},
      }}
    >
      {children}
    </StaffModeContext.Provider>
  );
};

export const useStaffMode = () => {
  const context = useContext(StaffModeContext);
  if (!context) {
    throw new Error('useStaffMode debe ser usado dentro de un StaffModeProvider');
  }
  return context;
};
